import mapboxgl from 'mapbox-gl';
import { SiteFeatures } from '../map-data-utils';
import { DensityMapConfigurator } from './';
import { ClockGranularity } from '../clock';

// ToDo: import centroid2D from helpers/geo and refactor code accordingly
// eslint-disable-next-line
import turfCentroid from '@turf/centroid';
import { ChartPivotRow } from '@cubejs-client/core';

/**
 * Find the average centerpoint from a collection
 * of polygons. Helps to center the map view over the
 * area of a site with the most spaces rather than the
 * geographical center of the overall boundary.
 * @param polygons
 * @return [x, y]
 *
 */
export const getFeatureCollectionCentroid = (
  featureCollection: SiteFeatures['featureCollection']
): [number, number] => {
  const mean = turfCentroid(featureCollection);
  return mean.geometry.coordinates as [number, number];
};

/**
 * Calculate the density of visitors within a space, according to
 * number of visitors per square metre. By default, 5 or more visitors
 * per square metre ins considered 100% density.
 * @param visitors the number of people in the space
 * @param area number the size of the space in square metres
 * @param maxPerSquareMeter number optional number of people per sqM for a space to be considered as 100% capacity
 * @return number space capacity, ranging from 0 (empty) to 1 (full). This value is rounded to two decimal places.
 */
export const getSpaceDensity = (visitors: number, area: number, maxPerSquareMeter: number): number => {
  const density = Math.min(visitors / (area * maxPerSquareMeter), 1);
  return Number(density.toFixed(2));
};

/**
 * 
 * @param t 
 * @returns 
 */
export const cameraEasing = (t: number) =>  {
  return t * 0.5;
}

export const getSpaceDensityLayerOptions = (source: string, level: number = 0): mapboxgl.Layer => (
  {
    id: `spaceDensity_${level}`,
    type: 'fill-extrusion',
    source,
    filter: ['==', 'level', level],
    paint: {
      'fill-extrusion-color': ['get', 'color'],
      'fill-extrusion-height': ['get', 'height'],
      'fill-extrusion-base': ['get', 'baseHeight'],
      'fill-extrusion-opacity': 1
    }
  }
)

export const getFloorLayerOptions = (
  source: string, 
  config: DensityMapConfigurator, 
  level: number = 0
  ): mapboxgl.Layer => ({
    id: `floor_${level}`,
    type: 'fill-extrusion',
    source,
    filter: ['==', 'level', level],
    paint: {
      'fill-extrusion-color': config.getLayerStyle(level).floorColor,
      'fill-extrusion-opacity': config.getLayerStyle(level).floorOpacity,
      'fill-extrusion-base': ['get', 'baseHeight'],
      'fill-extrusion-height': ['+', ['get', 'baseHeight'], 1]
    }
})


export const simpleEaseOutQuad = (x: number) => {
  return 1 - (1 - x) * (1 - x);
}

export type DensitySnapshot = {
  features: { [spaceRef: string]: number};
  range: {
    min: number;
    max: number;
  };
}

type GetDensitySnapshotOptions = {
  visitorDensity: ChartPivotRow[];
  timestamp?: string;
  granularity: ClockGranularity;
}

/**
   * Return an object of space density data for the point in time
   * that matches @timestamp
   * @param timestamp string value of point in time we are looking for
   * @returns object { [index: spaceRefId as string]:visitor as number}
   */
 export const getDensitySnapshot = (args: GetDensitySnapshotOptions): DensitySnapshot => {
  const {visitorDensity, timestamp, granularity } = args;
  const snapshot: DensitySnapshot = {
    features: {},
    range: {min: 100, max: 0}
  };
  
  visitorDensity.forEach(chartPivotRow => {

      const [rowTimestamp, rowSpaceRef] = chartPivotRow.xValues;
      const rowVisitors = chartPivotRow['ViewDensity.visitors']

      // if no timestamp is supplied then include all space from all timestamps in the dataset
      // for an aggregate view of total density over the entire query time period.
      // otherwise, filter out any datapoints which do not match the timestamp requested
      const includeSpace = !timestamp || rowTimestamp === timestamp;
    
      if (includeSpace) {
        const visitorCount = (snapshot.features[rowSpaceRef] || 0) + rowVisitors
        snapshot.features[rowSpaceRef] = visitorCount;
        snapshot.range.max = Math.max(snapshot.range.max, visitorCount);
        snapshot.range.min = Math.min(snapshot.range.min, visitorCount);
      }
  });
  return snapshot;
};